import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListSubheader,
  MenuItem,
  TextField,
} from '@mui/material';
import { customersAPI, projectsAPI, tasksAPI, timeEntriesAPI } from '../services/api';
import { localISODate } from '../utils/dates';
import { recentCustomerIds as recentIds, sortCustomersByRecent, splitTasks } from '../utils/timeEntryTarget';
import { useAuth } from '../contexts/AuthContext';

const today = () => localISODate();
const emptyForm = () => ({ customer_id: '', project_id: '', task_id: '', subtask_comment: '', hours: '', date: today() });
const toId = (v) => (v === '' || v == null ? null : parseInt(v, 10));

/**
 * Schnellerfassung vom Dashboard: Kunde, optional Projekt/Task, Stunden, Text.
 * Gleiche Regeln wie auf /timetracking – ohne Task landet die Zeit im Bericht
 * unter „Allgemeine Leistungen".
 */
const QuickTimeEntryDialog = ({ open, onClose, onSaved }) => {
  const { user } = useAuth();
  const [form, setForm] = useState(emptyForm());
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [recentCustomerIds, setRecentCustomerIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm(emptyForm());
    setError('');
    const since = new Date();
    since.setDate(since.getDate() - 60);
    Promise.all([
      customersAPI.getAll(),
      projectsAPI.getAll(),
      timeEntriesAPI.getAll({ user_id: user.id, start_date: localISODate(since) }),
    ]).then(([c, p, e]) => {
      setCustomers(c.data || []);
      setProjects(p.data || []);
      setRecentCustomerIds(recentIds(e.data, user.id));
    }).catch(() => setError('Daten konnten nicht geladen werden'));
  }, [open, user.id]);

  // Tasks des Kunden bzw. Projekts
  useEffect(() => {
    setTasks([]);
    if (!form.customer_id) return undefined;
    // Beim schnellen Kundenwechsel keine alte Antwort über die neue schreiben
    let current = true;
    const params = form.project_id ? { project_id: form.project_id } : { customer_id: form.customer_id };
    tasksAPI.getAll(params)
      .then((r) => current && setTasks(r.data || []))
      .catch(() => current && setTasks([]));
    return () => { current = false; };
  }, [form.customer_id, form.project_id]);

  const set = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'customer_id' ? { project_id: '', task_id: '' } : {}),
      ...(field === 'project_id' ? { task_id: '' } : {}),
    }));
  };

  const sortedCustomers = sortCustomersByRecent(customers, recentCustomerIds);
  const customerProjects = projects.filter((p) => p.customer_id === parseInt(form.customer_id, 10));
  const { tickets, tracker: trackerTasks } = splitTasks(tasks);
  const hours = parseFloat(String(form.hours).replace(',', '.'));
  const valid = form.customer_id && hours > 0 && form.subtask_comment.trim() && form.date;

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await timeEntriesAPI.create({
        customer_id: toId(form.customer_id),
        project_id: toId(form.project_id),
        task_id: toId(form.task_id),
        subtask_comment: form.subtask_comment.trim(),
        hours,
        date: form.date,
      });
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Zeiteintrag konnte nicht gespeichert werden');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 600, color: '#1e3a5f' }}>Zeit erfassen</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField select label="Kunde" value={form.customer_id} onChange={set('customer_id')} required autoFocus
                     SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 360 } } } }}>
            {sortedCustomers.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>{c.company_name}</MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField select label="Projekt (optional)" value={form.project_id} onChange={set('project_id')}
                       disabled={!form.customer_id} sx={{ flex: '1 1 200px' }}>
              <MenuItem value="">Kein Projekt</MenuItem>
              {customerProjects.map((p) => (
                <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>
              ))}
            </TextField>
            <TextField select label="Task (optional)" value={form.task_id} onChange={set('task_id')}
                       disabled={!form.customer_id} sx={{ flex: '1 1 200px' }}
                       SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 360 } } } }}>
              {[
                <MenuItem key="none" value="">Ohne Task (Allgemeine Leistungen)</MenuItem>,
                tickets.length > 0 && <ListSubheader key="h1">Tickets</ListSubheader>,
                ...tickets.map((t) => (
                  <MenuItem key={t.id} value={String(t.id)}>{t.task_id ? `${t.task_id} · ${t.name}` : t.name}</MenuItem>
                )),
                trackerTasks.length > 0 && <ListSubheader key="h2">Zeiterfassungs-Tasks</ListSubheader>,
                ...trackerTasks.map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>),
              ].filter(Boolean)}
            </TextField>
          </Box>
          <TextField label="Was wurde gemacht?" value={form.subtask_comment} onChange={set('subtask_comment')}
                     multiline minRows={3} required />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Stunden" value={form.hours} onChange={set('hours')} required
                       placeholder="1,5" inputProps={{ inputMode: 'decimal' }} sx={{ width: 140 }} />
            <TextField label="Datum" type="date" value={form.date} onChange={set('date')} required
                       InputLabelProps={{ shrink: true }} sx={{ width: 200 }} />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Abbrechen</Button>
        <Button variant="contained" onClick={handleSave} disabled={!valid || saving}
                sx={{ background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)', fontWeight: 600 }}>
          {saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickTimeEntryDialog;
